import { AblyProvider } from 'ably/react';
import { useEffect } from 'react';
import { AblyRealtime } from '../AblyRealtime';
import { AblyChannelProvider } from './AblyChannelProvider';
import { getLogLevel } from '../../common/getLogLevel';
import { createLogHandler } from './logHandler';
import { reportConnectionStateChange } from '../../v2/error-reporting/keplerAlerting';
import { jsx as _jsx } from "react/jsx-runtime";
const TRACKED_CONNECTION_STATES = new Set(['connected', 'connecting', 'disconnected', 'suspended', 'failed']);
const clientsMap = new Map();
const closeClientTimeoutMap = new Map();
const authCallbackMap = new Map();
function connectClient(ablyId) {
  clearTimeout(closeClientTimeoutMap.get(ablyId));
  closeClientTimeoutMap.delete(ablyId);
  const client = clientsMap.get(ablyId);
  client === null || client === void 0 || client.connect();
}
function queueCloseClient(ablyId) {
  closeClientTimeoutMap.set(ablyId,
  // Queue the client to be closed after a delay. The reason we delay here is
  // to handle scenarios where the component is unmounted and remounted
  // quickly, such as in strict mode. In this case, we want to allow the
  // connection to stay open until we're sure the component is not going
  // to remount
  window.setTimeout(() => {
    closeClientTimeoutMap.delete(ablyId);
    const client = clientsMap.get(ablyId);
    client === null || client === void 0 || client.close();
    clientsMap.delete(ablyId);
    authCallbackMap.delete(ablyId);
  }, 5000));
}
export function AblyRealtimeProvider({
  ablyId,
  authCallback,
  children,
  channels = [],
  clientOptions,
  shouldConnect = true,
  shouldSkipChannel
}) {
  authCallbackMap.set(ablyId, authCallback);
  if (!clientsMap.get(ablyId)) {
    const newAblyClient = new AblyRealtime(Object.assign({
      authCallback: (...args) => {
        var _authCallbackMap$get;
        return (_authCallbackMap$get = authCallbackMap.get(ablyId)) === null || _authCallbackMap$get === void 0 ? void 0 : _authCallbackMap$get(...args);
      },
      autoConnect: false,
      logLevel: getLogLevel(),
      logHandler: createLogHandler()
    }, clientOptions));
    newAblyClient.connection.on(stateChange => {
      if (TRACKED_CONNECTION_STATES.has(stateChange.current)) {
        reportConnectionStateChange(stateChange.current, stateChange.reason, ablyId);
      }
    });
    clientsMap.set(ablyId, newAblyClient);
  }
  const ablyClient = clientsMap.get(ablyId);
  useEffect(() => {
    if (shouldConnect) {
      connectClient(ablyId);
    }
  }, [ablyId, shouldConnect]);

  // Close the client when the provider unmounts
  useEffect(() => () => queueCloseClient(ablyId), [ablyId]);
  let childrenWithChannelProviders = children;
  for (const channel of channels) {
    var _shouldSkipChannel;
    const skip = !shouldConnect || ((_shouldSkipChannel = shouldSkipChannel === null || shouldSkipChannel === void 0 ? void 0 : shouldSkipChannel(channel)) !== null && _shouldSkipChannel !== void 0 ? _shouldSkipChannel : false);
    childrenWithChannelProviders = /*#__PURE__*/_jsx(AblyChannelProvider, {
      ablyId: ablyId,
      channelName: channel,
      skip: skip,
      children: childrenWithChannelProviders
    });
  }
  return /*#__PURE__*/_jsx(AblyProvider, {
    ablyId: ablyId,
    client: ablyClient,
    children: childrenWithChannelProviders
  });
}
AblyRealtimeProvider.displayName = 'AblyRealtimeProvider';